/**
 * Global product-card media slider.
 * <product-card-slider> wraps every product card's image gallery.
 * - Each image owns a baseline share of the progress bar: image i of N rests
 *   at (i+1)/N (e.g. image 1 of 4 rests at 25%).
 * - Hovering .card__inner only previews the next image when the slider is
 *   currently sitting on the first image: the bar animates forward one
 *   step, the slide changes, then it stops (no continuous autoplay).
 * - Hovering out reverses that same animation back to the first image.
 * - Whenever the current slide isn't the first one (whether a manual swipe
 *   landed there, or a hover preview is mid-flight), hover in/out does
 *   nothing. Swiping back to the first image re-arms the hover preview
 *   immediately - there's no persistent "has been touched" flag, only the
 *   current position matters.
 * - Clicking a color swatch replaces the whole gallery with that swatch's
 *   single variant image: the slide track and progress bar are hidden and
 *   only that one static image shows, with no further swiping.
 */
(function () {
  if (customElements.get('product-card-slider')) return;

  class ProductCardSlider extends HTMLElement {
    constructor() {
      super();
      this.activeIndex = 0;
      this.previewing = false;
    }

    connectedCallback() {
      this.track = this.querySelector('.card-slider__track');
      this.progressWrap = this.querySelector('.card-slider__progress');
      this.allSlides = Array.from(this.querySelectorAll('.card-slider__slide'));
      this.swatchPreview = this.querySelector('.card-slider__swatch-preview');
      this.swatchPreviewImg = this.swatchPreview ? this.swatchPreview.querySelector('img') : null;
      if (!this.track || !this.allSlides.length) return;

      this.refreshVisibleSlides();
      this.setActive(0, false);

      this.hoverTarget = this.closest('.card__inner') || this;
      this.hoverTarget.addEventListener('mouseenter', () => this.previewNext());
      this.hoverTarget.addEventListener('mouseleave', () => this.revertToRest());
      this.hoverTarget.addEventListener('focusin', () => this.previewNext());
      this.hoverTarget.addEventListener('focusout', () => this.revertToRest());

      this.addEventListener('dragstart', (event) => event.preventDefault());

      this.bindSwipe();
      this.bindSwatches();
    }

    refreshVisibleSlides() {
      this.slides = this.allSlides.filter((slide) => !slide.classList.contains('is-color-hidden'));
      this.fill = this.progressWrap ? this.progressWrap.querySelector('.card-slider__progress-fill') : null;
      if (this.progressWrap) {
        this.progressWrap.classList.toggle('is-single-slide', this.slides.length <= 1);
      }
    }

    setActive(index, animate) {
      if (!this.slides.length) return;
      const count = this.slides.length;
      this.activeIndex = ((index % count) + count) % count;
      if (this.track) {
        this.track.style.transition = animate === false ? 'none' : '';
        this.track.style.transform = 'translateX(-' + this.activeIndex * 100 + '%)';
        if (animate === false) {
          void this.track.offsetWidth;
          this.track.style.transition = '';
        }
      }
      this.paintProgress(animate);
    }

    paintProgress(animate) {
      if (!this.fill) return;
      this.fill.style.transition = animate === false ? 'none' : '';
      const total = this.slides.length;
      const pct = total <= 1 ? 0 : ((this.activeIndex + 1) / total) * 100;
      this.fill.style.width = pct + '%';
      if (animate === false) {
        void this.fill.offsetWidth;
        this.fill.style.transition = '';
      }
    }

    isShowingSwatchImage() {
      return !!(this.swatchPreview && this.swatchPreview.classList.contains('is-visible'));
    }

    previewNext() {
      if (this.isShowingSwatchImage() || this.slides.length <= 1 || this.activeIndex !== 0) return;
      this.previewing = true;
      this.setActive(1, true);
    }

    revertToRest() {
      if (!this.previewing) return;
      this.previewing = false;
      this.setActive(0, true);
    }

    bindSwipe() {
      let startX = 0;
      let startY = 0;
      let dragging = false;
      let moved = false;
      let deltaX = 0;

      const onDown = (event) => {
        if (this.isShowingSwatchImage() || this.slides.length <= 1) return;
        dragging = true;
        moved = false;
        deltaX = 0;
        startX = event.touches ? event.touches[0].clientX : event.clientX;
        startY = event.touches ? event.touches[0].clientY : event.clientY;
        this.track.style.transition = 'none';
      };

      const onMove = (event) => {
        if (!dragging) return;
        const x = event.touches ? event.touches[0].clientX : event.clientX;
        const y = event.touches ? event.touches[0].clientY : event.clientY;
        deltaX = x - startX;
        const deltaY = y - startY;
        if (Math.abs(deltaX) < Math.abs(deltaY)) return;
        if (Math.abs(deltaX) > 5) moved = true;
        if (moved && event.cancelable) event.preventDefault();
        const width = this.offsetWidth || 1;
        const basePct = -this.activeIndex * 100;
        const dragPct = (deltaX / width) * 100;
        this.track.style.transform = 'translateX(calc(' + basePct + '% + ' + dragPct + '%))';
      };

      const onUp = () => {
        if (!dragging) return;
        dragging = false;
        this.track.style.transition = '';
        if (moved) {
          this.justDragged = true;
          this.previewing = false;
          const threshold = (this.offsetWidth || 1) * 0.15;
          let target = this.activeIndex;
          if (deltaX <= -threshold) target = this.activeIndex + 1;
          else if (deltaX >= threshold) target = this.activeIndex - 1;
          this.setActive(target, true);
        }
        deltaX = 0;
      };

      this.addEventListener('pointerdown', onDown);
      this.addEventListener('pointermove', onMove);
      this.addEventListener('pointerup', onUp);
      this.addEventListener('pointercancel', onUp);
      this.addEventListener('pointerleave', () => {
        if (dragging) onUp();
      });
      this.addEventListener(
        'click',
        (event) => {
          if (this.justDragged) {
            event.preventDefault();
            event.stopPropagation();
            this.justDragged = false;
          }
        },
        true
      );
    }

    bindSwatches() {
      const wrapper = this.closest('.card-wrapper');
      if (!wrapper) return;
      const swatches = Array.from(wrapper.querySelectorAll('[data-color-value]'));
      if (!swatches.length) return;

      swatches.forEach((swatch) => {
        swatch.setAttribute('role', 'button');
        swatch.setAttribute('tabindex', '0');
        const activate = () => {
          const imageUrl = swatch.getAttribute('data-swatch-image') || '';
          if (!imageUrl) return;
          swatches.forEach((s) => s.classList.toggle('is-active', s === swatch));
          this.showSwatchImage(imageUrl);
        };
        swatch.addEventListener('click', activate);
        swatch.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            activate();
          }
        });
      });
    }

    showSwatchImage(url) {
      if (!this.swatchPreview || !this.swatchPreviewImg) return;
      this.previewing = false;
      this.swatchPreviewImg.src = url;
      this.swatchPreview.classList.add('is-visible');
      this.track.classList.add('is-hidden');
      if (this.progressWrap) this.progressWrap.classList.add('is-hidden');
    }
  }

  customElements.define('product-card-slider', ProductCardSlider);
})();

/**
 * Header search drawer.
 * Toggles a `.has-value` class on the drawer panel depending on whether the
 * search input is empty, so CSS can switch between the "popular searches /
 * popular products" state and the live predictive-search results state.
 */
(function () {
  function syncHasValue(input) {
    var panel = input.closest('.search-drawer__panel');
    if (!panel) return;
    panel.classList.toggle('has-value', input.value.trim().length > 0);
  }

  function bindSearchDrawer(input) {
    if (!input || input.dataset.searchDrawerBound) return;
    input.dataset.searchDrawerBound = 'true';
    syncHasValue(input);
    input.addEventListener('input', function () {
      syncHasValue(input);
    });
    if (input.form) {
      input.form.addEventListener('reset', function () {
        setTimeout(function () {
          syncHasValue(input);
        });
      });
    }
  }

  function initSearchDrawers() {
    document.querySelectorAll('.search-drawer .search__input').forEach(bindSearchDrawer);
  }

  document.addEventListener('DOMContentLoaded', initSearchDrawers);
  if (document.readyState === 'interactive' || document.readyState === 'complete') {
    initSearchDrawers();
  }
})();

/**
 * Header search drawer: reliable open/close slide.
 *
 * <details-modal> (assets/details-modal.js) only toggles the native
 * `open` attribute on the <details> it wraps. A <details> element does not
 * render its content at all while closed, so a CSS transition keyed off
 * `details[open] …` has nothing to interpolate from on repeat opens - the
 * browser just snaps straight to the end state, which is why the drawer
 * would slide in correctly the first time and then "teleport" open on
 * every open after that. Closing had the same problem in reverse: the
 * `open` attribute (and with it, the whole panel) disappears the instant
 * you close it, with no time for a reverse transition to play.
 *
 * This wraps that element's own open()/close() methods (rather than
 * touching the shared details-modal.js used elsewhere) to:
 * - force a reflow before adding `.is-open`, so the slide-in transition
 *   has a genuine "closed" frame to animate from on every open, and
 * - keep the native `open` attribute in place for the duration of the
 *   close transition, so the drawer can slide back out before it unmounts.
 */
(function () {
  function bindSearchDrawerAnimation(modal) {
    if (modal.dataset.searchDrawerAnimBound) return;
    var details = modal.querySelector('details');
    var inner = modal.querySelector('.search-drawer__inner');
    if (!details || !inner || typeof modal.open !== 'function' || typeof modal.close !== 'function') return;
    modal.dataset.searchDrawerAnimBound = 'true';

    var originalOpen = modal.open.bind(modal);
    var originalClose = modal.close.bind(modal);
    var closeTimer = null;

    modal.open = function (event) {
      clearTimeout(closeTimer);
      modal.classList.remove('is-closing');
      originalOpen(event);
      // Force the browser to paint the off-screen position first.
      void inner.offsetWidth;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          modal.classList.add('is-open');
        });
      });
    };

    modal.close = function (focusToggle) {
      if (!modal.classList.contains('is-open')) {
        originalClose(focusToggle);
        return;
      }
      modal.classList.remove('is-open');
      modal.classList.add('is-closing');

      var settled = false;
      var onEnd = function (event) {
        if (event.target !== inner || event.propertyName !== 'transform') return;
        finish();
      };
      var finish = function () {
        if (settled) return;
        settled = true;
        inner.removeEventListener('transitionend', onEnd);
        clearTimeout(closeTimer);
        modal.classList.remove('is-closing');
        originalClose(focusToggle);
      };

      inner.addEventListener('transitionend', onEnd);
      // Safety net in case transitionend never fires (reduced motion, etc.)
      closeTimer = setTimeout(finish, 450);
    };

    // The "X" button's click handler is wired up once, inside
    // details-modal.js's constructor, as `button.addEventListener('click',
    // this.close.bind(this))` - that .bind() captures whatever `this.close`
    // pointed to at that exact moment, which runs before this script has
    // had a chance to patch it above, so the button was permanently stuck
    // calling the original, unanimated close(). Clicking the overlay works
    // because it calls `this.close()` fresh each time instead of using a
    // pre-bound reference. Cloning the button drops that stale listener (a
    // clone carries no addEventListener listeners with it); the fresh node
    // gets our own listener that calls the now-patched close(), so the "X"
    // animates the drawer shut exactly like the overlay already does.
    var closeButton = modal.querySelector('.search-drawer__close-button');
    if (closeButton) {
      var freshCloseButton = closeButton.cloneNode(true);
      closeButton.replaceWith(freshCloseButton);
      freshCloseButton.addEventListener('click', function () {
        modal.close();
      });
    }
  }

  function initSearchDrawerAnimations() {
    document.querySelectorAll('.search-drawer').forEach(bindSearchDrawerAnimation);
  }

  document.addEventListener('DOMContentLoaded', initSearchDrawerAnimations);
  if (document.readyState === 'interactive' || document.readyState === 'complete') {
    initSearchDrawerAnimations();
  }
})();
